import type {
	OrderCallResponseDto,
	OrderListRowResponseDto,
} from "Infrastructure/Api/Api";
import {
	getApiUrl,
	type MarketResponseDto,
	OrderCallStatus,
	OrderDirection,
	type OrderFilterDto,
	OrderStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { formatCurrency } from "Infrastructure/Utilities/currencyFormat";
import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { defaultTableConfig } from "Infrastructure/Utilities/defaultTableConfig";
import { useOrdersList } from "Order/Api/Query/useOrdersListQuery";
import { useFollowupStatus } from "Order/Hooks/useFollowupStatus";
import { OrderPaths } from "Order/Routes/Routing";
import { useTableFilter } from "Shared/Hooks/useTableFilter";
import {
	CallEnd,
	Edit,
	Phone,
	PhoneInTalk,
	Visibility,
	Work,
} from "@mui/icons-material";
import type { ChipProps } from "@mui/material";
import { Box, Chip, IconButton, Stack, Typography } from "@mui/material";
import {
	createMRTColumnHelper,
	type MRT_DensityState,
} from "material-react-table";
import { Link, useNavigate } from "react-router";

//TODO[JAKUB]: Move this type into shared types somewhere to components
enum TableActionType {
	Button = "button",
	Link = "link",
}

type TableAction = {
	type: TableActionType;
	action?: () => void;
	icon: JSX.Element;
	href?: string;
};

const getOrderCallStatusIcon = (orderCalls: OrderCallResponseDto[]) => {
	let hasAnswered = false;
	let hasDeclinedOnly = false;

	if (orderCalls?.length === 0) {
		return <Phone fontSize="small" />;
	}

	hasAnswered = orderCalls?.some(
		(call) => call.status === OrderCallStatus.answered,
	);
	hasDeclinedOnly = orderCalls?.every(
		(call) => call.status === OrderCallStatus.no_answer,
	);

	if (hasAnswered) {
		return <PhoneInTalk fontSize="small" />;
	}
	if (hasDeclinedOnly) {
		return <CallEnd fontSize="small" />;
	}
};

const orderStatusChipColor: Record<OrderStatus, ChipProps["color"]> = {
	[OrderStatus.done]: "success",
	[OrderStatus.in_progress]: "progress",
	[OrderStatus.confirmed]: "warning",
	[OrderStatus.cancelled]: "error",
	[OrderStatus.pending]: "default",
};

type OrderTableRow = OrderListRowResponseDto & {
	price?: number;
	actions?: TableAction[];
	market?: MarketResponseDto | null;
	userFullName?: string;
	contact: {
		phoneNumber?: string | null;
		email?: string | null;
		orderCalls: OrderCallResponseDto[];
	};
};

const {
	detail: { info: orderTableResource },
	shared: sharedResource,
	selectOptions: selectOptionsResource,
} = Resources.base.order;

export const useOrderTable = (filter: OrderFilterDto) => {
	const { t } = useResource();
	const navigate = useNavigate();
	const { renderFollowupStatusIcon } = useFollowupStatus();

	const { pageIndex, pageLimit, sorting, setSearchParams } = useTableFilter();

	const columnHelper = createMRTColumnHelper<OrderTableRow>();
	const { data, isLoading } = useOrdersList({
		...filter,
		pageIndex,
		pageLimit,
		orderBy: sorting[0]?.id,
		orderDirection: sorting[0]?.desc ? OrderDirection.desc : OrderDirection.asc,
	});

	const { formatDate } = useDateFormat();

	const tableData = (data?.data ?? []).map(
		(order: OrderListRowResponseDto) => ({
			...order,
			contact: {
				phoneNumber: order.customerPhone,
				email: order.customerEmail,
				orderCalls: order.orderCalls,
			},
			price: order.price,
			materialPrice: order.materialPrice,
			actions: [
				{
					type: TableActionType.Link,
					href: OrderPaths.Detail.path(order.id),
					icon: <Visibility fontSize="small" />,
				},
				{
					type: TableActionType.Link,
					href: OrderPaths.Edit.path(order.id),
					icon: <Edit fontSize="small" />,
				},
			],
		}),
	);

	const columns = [
		columnHelper.accessor((row) => row.vertical, {
			id: "vertical",
			header: "",
			enableColumnActions: false,
			enableSorting: false,
			maxSize: 30,
			minSize: 30,
			size: 30,
			grow: 0,
			Cell: ({ row }) => (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "20px",
						height: "20px",
					}}
				>
					<img
						src={`${getApiUrl()}${row.original.vertical.iconPath}`}
						alt={`${row.original.vertical.name} icon`}
						width="16px"
						height="16px"
						style={{ objectFit: "contain" }}
					/>
				</Box>
			),
		}),
		columnHelper.accessor((row) => row.b2b, {
			id: "b2bFlag",
			header: "",
			enableColumnActions: false,
			enableSorting: false,
			maxSize: 24,
			minSize: 24,
			size: 24,
			grow: 0,
			Cell: ({ row }) =>
				row.original.b2b ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: "20px",
							height: "20px",
						}}
					>
						<Work fontSize="small" sx={{ color: "orange" }} />
					</Box>
				) : null,
		}),
		columnHelper.accessor((row) => row.followUpAgentStatus, {
			id: "followUpAgentStatus",
			header: "",
			enableColumnActions: false,
			enableSorting: false,
			maxSize: 20,
			minSize: 20,
			size: 20,
			grow: 0,
			Cell: ({ row }) => {
				const robotIcon = renderFollowupStatusIcon(
					row.original?.followUpAgentStatus || "",
				);

				return row.original?.followUpAgentStatus ? robotIcon : null;
			},
		}),
		columnHelper.accessor((row) => row.id, {
			id: "id",
			header: t(orderTableResource.id),
			enableColumnActions: false,
			enableSorting: true,
			maxSize: 10,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.id}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.dateCreated, {
			id: "dateCreated",
			header: t(orderTableResource.dateCreated),
			enableColumnActions: false,
			enableSorting: true,
			maxSize: 100,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.dateCreated && formatDate(row.original.dateCreated)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.customerName, {
			id: "customerName",
			header: t(orderTableResource.customerName),
			enableColumnActions: false,
			enableSorting: false,
			maxSize: 100,
			grow: 0,
			Cell: ({ row }) => (
				<>
					<Typography variant="tableCell" fontWeight="bold">
						{row.original.customerName}
					</Typography>
					<Stack direction="row" spacing={0.5} alignItems="center">
						<Typography variant="tableCell">
							{row.original.region?.name}
						</Typography>
					</Stack>
				</>
			),
		}),
		columnHelper.accessor((row) => row.contact, {
			id: "contact",
			header: t(orderTableResource.contact),
			enableColumnActions: false,
			enableSorting: false,
			maxSize: 100,
			grow: 0,
			Cell: ({ row }) => (
				<Stack direction="row" spacing={1} alignItems="center">
					<Box>{getOrderCallStatusIcon(row.original.orderCalls)}</Box>
					<Stack direction="column">
						<Typography variant="tableCell">
							{row.original.customerPhone}
						</Typography>
						<Typography variant="tableCell">
							{row.original.customerEmail}
						</Typography>
					</Stack>
				</Stack>
			),
		}),
		columnHelper.accessor((row) => row.status, {
			id: "status",
			header: t(orderTableResource.status),
			enableColumnActions: false,
			enableSorting: true,
			maxSize: 100,
			grow: 0,
			Cell: ({ row }) => (
				<Stack direction="column" rowGap={0.5}>
					<Chip
						label={t(selectOptionsResource.orderStatus[row.original.status])}
						size="small"
						variant="outlined"
						color={orderStatusChipColor[row.original.status]}
						sx={{ width: "80px" }}
					/>
					{row.original.hasExamination && (
						<Chip
							label={t(orderTableResource.examination)}
							size="small"
							variant="outlined"
							color="info"
							sx={{ width: "80px" }}
						/>
					)}
					{row.original.hasClaim && (
						<Chip
							label={t(orderTableResource.claim)}
							size="small"
							variant="outlined"
							color="error"
							sx={{
								width: "80px",
							}}
						/>
					)}
				</Stack>
			),
		}),
		columnHelper.accessor((row) => row.dateStarting, {
			id: "dateStarting",
			header: t(orderTableResource.dateStarting),
			enableColumnActions: false,
			enableSorting: true,
			maxSize: 100,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.dateStarting && formatDate(row.original.dateStarting)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.price, {
			id: "price",
			header: t(orderTableResource.price),
			enableColumnActions: false,
			enableSorting: true,
			maxSize: 100,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.price
						? formatCurrency(
								row.original.market?.country,
								row.original.market?.currency,
								(row.original.price ?? 0) + (row.original.materialPrice ?? 0),
							)
						: ""}
				</Typography>
			),
		}),

		columnHelper.accessor((row) => row.partnerName, {
			id: "partnerName",
			header: t(orderTableResource.partnerName),
			enableColumnActions: false,
			enableSorting: false,
			maxSize: 100,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.partnerName}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.userFullName, {
			id: "manager",
			header: t(orderTableResource.manager),
			enableColumnActions: false,
			enableSorting: false,
			maxSize: 100,
			grow: 0,
			Cell: ({ row }) => (
				<>
					{row.original.userId ? (
						<Typography variant="tableCell">
							{`${row.original.userName} ${row.original.userSurname}`}
						</Typography>
					) : (
						"-"
					)}
				</>
			),
		}),
		columnHelper.accessor((row) => row.dateNextAction, {
			id: "dateNextAction",
			header: t(orderTableResource.dateNextAction),
			enableColumnActions: false,
			enableSorting: true,
			maxSize: 100,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.dateNextAction &&
						formatDate(row.original.dateNextAction)}
				</Typography>
			),
		}),
		{
			id: "actions",
			header: t(sharedResource.actions),
			enableColumnActions: false,
			enableSorting: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }) => (
				<Stack direction="row" columnGap={1}>
					{row.original.actions?.map((action: TableAction, index: number) => {
						if (action.type === TableActionType.Button && !!action.action) {
							return (
								<IconButton
									key={index}
									onClick={action.action}
									size="small"
									sx={{
										background: "transparent",
										transition: "all 0.15s ease",
										"&:hover": { background: "transparent", opacity: 0.5 },
									}}
								>
									{action.icon}
								</IconButton>
							);
						}

						if (action.type === TableActionType.Link && !!action.href) {
							return (
								<Link key={index} to={action.href}>
									<IconButton
										key={index}
										size="small"
										sx={{
											background: "transparent",
											transition: "all 0.15s ease",
											"&:hover": { background: "transparent", opacity: 0.5 },
										}}
									>
										{action.icon}
									</IconButton>
								</Link>
							);
						}

						return (
							<IconButton
								key={index}
								size="small"
								sx={{
									background: "transparent",
									transition: "all 0.15s ease",
									"&:hover": { background: "transparent", opacity: 0.5 },
								}}
							>
								{action.icon}
							</IconButton>
						);
					})}
				</Stack>
			),
		},
	];

	const handleSortingChange = (updater) => {
		const newSorting = updater(sorting);
		const cleanedFilter = Object.fromEntries(
			Object.entries(filter).filter(([_, value]) => value !== undefined),
		);

		setSearchParams({
			...cleanedFilter,
			sorting: newSorting,
			pageIndex,
			pageLimit,
		});
	};

	const handlePaginationChange = (updater) => {
		const cleanedFilter = Object.fromEntries(
			Object.entries(filter).filter(([_, value]) => value !== undefined),
		);

		const newPagination = updater({ pageIndex, pageLimit });
		setSearchParams({ ...cleanedFilter, ...newPagination });
	};

	const tableOptions = {
		...defaultTableConfig,
		columns,
		data: tableData,
		state: {
			showSkeletons: isLoading,
			isLoading,
			pagination: {
				pageSize: pageLimit,
				pageIndex: pageIndex,
			},
			sorting: sorting,
		},
		initialState: {
			density: "compact" as MRT_DensityState,
			columnVisibility: {
				type: false,
			},
		},
		enableColumnActions: false,
		enableTopToolbar: false,
		onSortingChange: handleSortingChange,
		manualSorting: true,
		muiPaginationProps: {
			showRowsPerPage: false,
			showFirstButton: false,
			showLastButton: false,
		},
		manualPagination: true,
		onPaginationChange: handlePaginationChange,
		rowCount: data?.metaData?.count ?? 0,
		muiTableBodyRowProps: ({ row }) => ({
			onDoubleClick: () => {
				const orderId = row.original.id;
				navigate(OrderPaths.Detail.path(orderId));
			},
		}),
	};

	return tableOptions;
};
